import React, { useRef, useState } from 'react';
import { ProjectService } from '@/api/project';
import { IModuleSchema } from '@/api/module';
import { Loading } from '@/components/Loading';
import { Section } from '@/components/Section';
import { TestCaseModule } from '@/components/module/TestCaseModule';
import { CreateButton } from '@/components/shared/button/CreateButton';
import { OutlineButton } from '@/components/shared/button/OutlineButton';
import { ExtendedTableLowCode, ExtendedTableLowCodeRef } from '@/components/shared/table/ExtendedTableLowCode';
import { DeleteTestCase } from '@/components/testCase/DeleteTestCase';
import { ExportTestCase } from '@/components/testCase/ExportTestCase';
import { ImportTestCase } from '@/components/testCase/ImportTestCase';
import { useCustomSpec } from '@/utils/hooks/useCustomSpec';
import { FaArchive, FaRegCopy } from 'react-icons/fa';
import { useNavigate, useParams } from 'react-router-dom';
import { IFilterSpec } from '@/components/shared/filter/TableFilterPanel';
import { DownloadTemplateTestCase } from '@/components/testCase/DownloadTemplateTestCase';
import { useAccess } from '@/utils/hooks/useAccess';
import { useIsExternal } from '@/utils/hooks/useUserType';
import { Provider } from 'react-redux';
import moduleStore from '@/states/moduleStore';
import { CloneModule } from '@/components/module/CloneModule';
import { LinkButton } from '@/components/shared/button/LinkButton';
import { BiSolidShow } from 'react-icons/bi';
import { FaPen } from 'react-icons/fa6';
import { MoveTestCase } from '@/components/module/MoveTestCase';

export const ListTestCase: React.FC = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const ref = useRef<ExtendedTableLowCodeRef>(null);

  const isViewer = useAccess(id!);
  const isExternal = useIsExternal();
  const isReadOnly = isViewer || isExternal;

  const [selectedModule, setSelectedModule] = useState<IModuleSchema | undefined>(undefined);
  const [selectedTestCase, setSelectedTestCase] = useState<string[]>([]);
  const [moduleRefetch, setModuleRefetch] = useState<() => void>(() => {
  });

  const spec = useCustomSpec('/api/crud/test-case', {
    columns: [
      { key: 'case_code', label: 'Case Code' },
      { key: 'title', label: 'Title' },
      { key: 'total_step', label: 'Step' },
      { key: 'pic', label: 'PIC' },
      { key: 'created_by', label: 'Created By' },
      { key: 'created_at', label: 'Created Date' },
    ],
  });

  const renderCustomCell = ({ name, rowValue, defaultCell }: any) => {
    if (name === 'pic') {
      const picUser = rowValue.pic ?? rowValue.module?.pic;
      if (!picUser) return '-';
      return (
        `${picUser.first_name ?? ''} ${picUser.last_name ?? ''}`.trim() ||
        picUser.email
      );
    }

    if (name === 'created_by') {
      const user = rowValue.created_by;
      if (!user) return '-';
      return (
        `${user.first_name ?? ''} ${user.last_name ?? ''}`.trim() || user.email
      );
    }

    if (name === 'created_at') {
      if (!rowValue.created_at) return '-';
      const date = new Date(rowValue.created_at);
      if (isNaN(date.getTime())) return rowValue.created_at;

      const day = String(date.getDate()).padStart(2, '0');
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const year = date.getFullYear();

     
      return `${day}-${month}-${year}`;
    }

    return defaultCell;
  };

  const { data: projectData, isFetching: isProjectFetching } =
    ProjectService.useGet(id);

  const toArchive = () => navigate('archive');

  const filterSpec: IFilterSpec[] = [
    { name: 'case_code', label: 'Case Code', type: 'textarea' },
    { name: 'title', label: 'Title', type: 'text' },
    { name: 'created_by', label: 'Created By', type: 'text' },
    { name: 'created_at', label: 'Created Date', type: 'date' },
  ];

  if (isProjectFetching) {
    return (
      <Section>
        <Loading />
      </Section>
    );
  }

  return (
    <Provider store={moduleStore}>
      <Section
        title={`Test Case Project - ${projectData?.title}`}
        actions={
          !isReadOnly ? (
            <div className="flex flex-row gap-3">
              <OutlineButton icon={<FaArchive />} onClick={toArchive}>
                Archive
              </OutlineButton>

              <ExportTestCase id={id} projectName={projectData?.title} />

              <ImportTestCase id={id}></ImportTestCase>

              <DownloadTemplateTestCase />

              <CreateButton label="Add Test Case" />
            </div>
          ) : (
            <></>
          )
        }
      >
        <div className="flex flex-row min-h-screen">
          <div className="w-[30%] ">
            <TestCaseModule
              isViewer={isReadOnly}
              project={projectData!}
              onSelectedModule={module => {
                setSelectedModule(module);
                ref.current?.filter({
                  module_id: module.id,
                });
                ref.current?.show();
              }}
              setModuleRefetch={setModuleRefetch}
            />
          </div>

          <div className="w-[70%]">
            {!selectedModule && (
              <div className="py-6 text-center">
                Select a epic or user story
              </div>
            )}

            <ExtendedTableLowCode
              ref={ref}
              hidden
              spec={spec}
              customCell={renderCustomCell}
              defaultFilter={{
                module_id: selectedModule?.id,
                project_id: id,
                is_archive: 'false',
              }}
              defaultSort="case_code"
              defaultSortOrder="asc"
              customButtonCreate={() =>
                !isReadOnly ? (
                  <>
                    <MoveTestCase
                      onSuccess={() => {
                        setSelectedTestCase([]);
                        ref.current?.refetch();
                        moduleRefetch();
                      }}
                      testcases={selectedTestCase}
                      triggerButtonType="list"
                    />

                    <CloneModule
                      onSuccess={() => {
                        ref.current?.refetch();
                        moduleRefetch();
                      }}
                      module={selectedModule}
                      triggerButtonType="list"
                    />
                  </>
                ) : (
                  <></>
                )
              }
              customActionCell={data =>
                !isReadOnly ? (
                  <>
                    <LinkButton
                      path={`${data.id}/clone`}
                      className="bg-green-600"
                      icon={<FaRegCopy size={20} />}
                    />

                    <LinkButton
                      path={data.id}
                      icon={<BiSolidShow size={20} />}
                    />

                    <LinkButton
                      path={`${data.id}/edit`}
                      icon={<FaPen size={15} />}
                    />

                    <DeleteTestCase
                      id={data.id}
                      onSuccess={() => ref.current?.refetch()}
                    />
                  </>
                ) : (
                  <LinkButton path={data.id} icon={<BiSolidShow size={20} />} />
                )
              }
              filterSpec={filterSpec}
              showFilter
              showCheckbox
              selectedRow={selectedTestCase}
              setSelectedRow={setSelectedTestCase}
            />
          </div>
        </div>
      </Section>
    </Provider>
  );
};
